import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import SchoolServiceAdmin from '../../services/SchoolServiceAdmin';

const CreateDeleteAbsenceListForTeacher = ({ allTeachers }) => {
  const [responseMsg, setResponseMsg] = useState('');
  const [responseType, setResponseType] = useState('info');
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);

  const [attendanceDates, setAttendanceDates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auto-hide messages
  useEffect(() => {
    if (responseMsg) {
      const base = 3000;
      const extra = responseMsg.length * 30; // adjust speed
      const duration = Math.min(base + extra, 30000); // max 20 sec

      const timer = setTimeout(() => setResponseMsg(''), duration);

      return () => clearTimeout(timer);
    }
  }, [responseMsg]);

  /** ---------------------------------------------------
   * FETCH ATTENDANCE FOR A WHOLE MONTH
   -----------------------------------------------------*/
  const fetchAttendanceForMonth = async (teacherId, dateObj) => {
    if (!teacherId) return;

    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();

    const start = new Date(year, month, 1)
      .toISOString()
      .split('T')[0];
    const end = new Date(year, month + 1, 0)
      .toISOString()
      .split('T')[0];

    try {
      setLoading(true);

      const response =
        await SchoolServiceAdmin.getTeacherAttendanceCheckOnDateRange(
          start,
          end,
          teacherId,
        );

      const teacherData = response.data?.[0];

      const dates =
        teacherData?.dateChecks?.map((dc) => dc.dateEntity.date) ||
        [];

      setAttendanceDates(dates);
    } catch (error) {
      console.error(error);
      setAttendanceDates([]);
    } finally {
      setLoading(false);
    }
  };

  /** When teacher changes → fetch for the current month */
  const handleOptionChange = (event) => {
    const teacherId = event.target.value;
    setSelectedOption(teacherId);

    const baseDate = selectedDate || new Date();
    if (teacherId) fetchAttendanceForMonth(teacherId, baseDate);
    else setAttendanceDates([]);
  };

  /** CREATE LIST */
  const createNewAbsenceListForTeacher = async () => {
    if (!selectedOption) {
      setResponseMsg('⚠️ Please select a teacher.');
      setResponseType('error');
      return;
    }
    if (!selectedDate) {
      setResponseMsg('⚠️ Please choose a date.');
      setResponseType('error');
      return;
    }

    const dateString = selectedDate.toLocaleDateString('en-CA');

    try {
      await SchoolServiceAdmin.createNewAbsenceListForTeacherOnDate(
        selectedOption,
        dateString,
      );

      setResponseMsg(`✅ Created absence list for ${dateString}.`);
      setResponseType('success');

      fetchAttendanceForMonth(selectedOption, selectedDate);
    } catch (error) {
      console.error(error);
      setResponseMsg('❌ Failed to create.');
      setResponseType('error');
    }
  };

  const createAbsenceListForAllTeachers = async () => {
    if (!selectedDate) {
      setResponseMsg('⚠️ Please choose a date.');
      setResponseType('error');
      return;
    }

    const dateString = selectedDate.toLocaleDateString('en-CA');

    try {
      const response =
        await SchoolServiceAdmin.createAbsenceListForAllTeachers(
          dateString,
        );

      setResponseMsg(response.data);

      setResponseType('success');

      fetchAttendanceForMonth(selectedOption, selectedDate);
    } catch (error) {
      console.error(error);
      setResponseMsg(
        error?.response?.data || '❌ Failed to create lists.',
      );
      setResponseType('error');
    }
  };

  const deleteAbsenceListForAllTeachers = async () => {
    if (!selectedDate) {
      setResponseMsg('⚠️ Please choose a date.');
      setResponseType('error');
      return;
    }

    const dateString = selectedDate.toLocaleDateString('en-CA');

    try {
      const response =
        await SchoolServiceAdmin.deleteAbsenceListForAllTeachers(
          dateString,
        );

      setResponseMsg(response.data);
      setResponseType('success');

      fetchAttendanceForMonth(selectedOption, selectedDate);
    } catch (error) {
      console.error(error);

      setResponseMsg(
        error?.response?.data || '❌ Failed to delete lists.',
      );
      setResponseType('error');
    }
  };

  /** DELETE LIST */
  const deleteAbsenceListForTeacher = async () => {
    if (!selectedOption) {
      setResponseMsg('⚠️ Please select a teacher.');
      setResponseType('error');
      return;
    }
    if (!selectedDate) {
      setResponseMsg('⚠️ Please choose a date.');
      setResponseType('error');
      return;
    }

    const dateString = selectedDate.toLocaleDateString('en-CA');

    try {
      await SchoolServiceAdmin.deleteAbsenceListForTeacherOnDate(
        selectedOption,
        dateString,
      );

      setResponseMsg(`🗑️ Deleted absence list for ${dateString}.`);
      setResponseType('success');

      fetchAttendanceForMonth(selectedOption, selectedDate);
    } catch (error) {
      console.error(error);
      setResponseMsg('❌ Failed to delete.');
      setResponseType('error');
    }
  };

  /** Render day with green dot */
  const renderDayContents = (day, date) => {
    const formatted = date.toLocaleDateString('en-CA');
    const hasAbsenceList = attendanceDates.includes(formatted);

    return (
      <div className="relative w-full h-full text-center overflow-visible">
        <span className="inline-block leading-none">{day}</span>

        {hasAbsenceList && (
          <span
            className="
            absolute left-1/2 -translate-x-1/2 
            bottom-0
            w-4/5 
            h-1.5 
            bg-green-600 
            rounded-sm 
            pointer-events-none
          "
          />
        )}
      </div>
    );
  };

  /** UI Helpers */
  const getAlertStyle = () => {
    switch (responseType) {
      case 'success':
        return 'bg-green-50 text-green-800 border-green-300';
      case 'error':
        return 'bg-red-50 text-red-800 border-red-300';
      default:
        return 'bg-blue-50 text-blue-800 border-blue-300';
    }
  };

  const getAlertIcon = () => {
    switch (responseType) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'error':
        return (
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
        );
      default:
        return (
          <InformationCircleIcon className="h-6 w-6 text-blue-600" />
        );
    }
  };

  return (
    <div className="flex justify-center items-center w-full py-8">
      <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6 w-full">
        <h1 className="text-2xl font-semibold text-cyan-900 text-center mb-6">
          Create / Delete Absence List for a Teacher
          <br />
          <span className="text-xl text-gray-600 font-normal">
            إنشاء/حذف قائمة الحضور والغياب للمعلمة
          </span>
        </h1>

        <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full">
          {/* Teacher Select */}
          <div className="flex-1">
            <label className="block text-sm font-semibold mb-1">
              Select Teacher
            </label>
            <select
              value={selectedOption}
              onChange={handleOptionChange}
              className="w-full border px-3 py-2 rounded-md"
            >
              <option value="">-- Choose a teacher --</option>
              {allTeachers?.map((t) => (
                <option key={t.id} value={t.id}>
                  {`${t.id} | ${t.firstName} - ${t.username} | ${t.classAbbrev} | ${t.teacherClassDaysOfWeek?.join(', ') || 'No days'}`}
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div className="flex-1">
            <label className="block text-sm font-semibold mb-1">
              Select Date
            </label>

            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                if (selectedOption)
                  fetchAttendanceForMonth(selectedOption, date);
              }}
              onMonthChange={(monthDate) => {
                if (selectedOption)
                  fetchAttendanceForMonth(selectedOption, monthDate);
              }}
              placeholderText="Choose a date"
              className="w-full border px-3 py-2 rounded-md"
              renderDayContents={renderDayContents}
            />
          </div>

          <div className="flex flex-col gap-3">
            {/* Row 1 */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={createNewAbsenceListForTeacher}
                className="rounded-md bg-green-600 text-white px-5 py-2 font-semibold shadow-sm hover:bg-green-700"
              >
                Create List For Teacher
              </button>
              <button
                onClick={deleteAbsenceListForTeacher}
                className="rounded-md bg-red-600 text-white px-5 py-2 font-semibold shadow-sm hover:bg-red-700"
              >
                Delete List For Teacher
              </button>
            </div>

            {/* Row 2 */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={createAbsenceListForAllTeachers}
                className="rounded-md bg-blue-600 text-white px-5 py-2 font-semibold shadow-sm hover:bg-blue-700"
              >
                Create Lists For All Having Class At Same Date
              </button>
              <button
                onClick={deleteAbsenceListForAllTeachers}
                className="rounded-md bg-purple-600 text-white px-5 py-2 font-semibold shadow-sm hover:bg-purple-700"
              >
                Delete All Lists For Date
              </button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {responseMsg && (
          <div
            className={`mt-6 border rounded-md px-4 py-3 flex items-center gap-3 ${getAlertStyle()}`}
          >
            {getAlertIcon()}
            <span
              style={{ whiteSpace: 'pre-line' }}
              className="font-medium"
            >
              {responseMsg}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateDeleteAbsenceListForTeacher;
